import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  CubeIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import stockService, { Magasin, Produit, MouvementStock } from '@/services/stockService';
import EditStockThresholdsModal from '@/components/modals/EditStockThresholdsModal';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Interfaces basées sur les modèles backend
interface StockItem {
  id: number;
  produit: number;
  magasin: number;
  quantite_entree: number;
  quantite_sortie: number;
  stock_actuel: number;
  date_mise_a_jour: string;
  created_at: string;
  updated_at: string;
  // Relations
  produit_details?: Produit;
  magasin_details?: Magasin;
  // Champs du backend (noms simples)
  produit_nom?: string;
  produit_type_display?: string;
  produit_categorie?: string;
  produit_unite?: string;
  produit_prix_unitaire?: number;
  magasin_nom?: string;
  // Champs calculés pour l'affichage
  status?: 'normal' | 'low' | 'critical' | 'full';
  minThreshold?: number;
  maxCapacity?: number;
  lastMovement?: {
    type: 'in' | 'out';
    quantity: number;
    date: string;
    reason: string;
  };
}

interface StockMovement {
  id: number;
  reference: string;
  produit: number;
  pesee: number;
  magasin_source: number | null;
  magasin_destination: number | null;
  sens: 'ENTREE' | 'SORTIE';
  quantite: number;
  type_sortie: string | null;
  motif: string;
  observations?: string;
  date_mouvement: string;
  utilisateur: number;
  operateur?: number;
  created_at: string;
  updated_at: string;
  // Relations
  produit_details?: Produit;
  pesee_details?: any;
  // Champs du backend (noms simples)
  produit_nom?: string;
  produit_type_display?: string;
  produit_categorie?: string;
  produit_description?: string;
  pesee_reference?: string;
  pesee_poids_net?: number;
  nombre_sacs?: number;
  magasin_source_nom?: string;
  magasin_source_type?: string;
  magasin_destination_nom?: string;
  magasin_destination_type?: string;
  operateur_nom?: string;
  operateur_email?: string;
  sens_display?: string;
  type_sortie_display?: string;
  date_mouvement_display?: string;
  // Champs transformés pour l'affichage
  productName?: string;
  productType?: string;
  productCategory?: string;
  productDescription?: string;
  peseeWeight?: number;
  type?: 'in' | 'out';
  user?: string;
  userEmail?: string;
  location?: string;
  locationType?: string;
  date?: string;
  direction?: string;
  quantity?: number;
  // Champs du camion
  camion_immatriculation?: string;
  camion_marque?: string;
  camion_modele?: string;
  // Champs du chauffeur
  chauffeur_nom?: string;
  chauffeur_prenom?: string;
  chauffeur_telephone?: string;
  // Champs du fournisseur
  fournisseur_nom?: string;
  fournisseur_adresse?: string;
  fournisseur_contact?: string;
  // Champs du client
  client_nom?: string;
  client_adresse?: string;
  client_telephone?: string;
  // Champs de l'exportateur
  exportateur_nom?: string;
  exportateur_adresse?: string;
  exportateur_telephone?: string;
  magasin_source_details?: Magasin;
  magasin_destination_details?: Magasin;
  utilisateur_details?: any;
}

interface WarehouseStats {
  id: number;
  nom: string;
  code: string;
  type_magasin: string;
  capacite_maximale: number | null;
  surface: number | null;
  statut: string;
  est_operational: boolean;
  // Champs calculés
  usedCapacity?: number;
  productCount?: number;
  criticalItems?: number;
}

interface DailyStockMovement {
  date: string;
  entrees: any[];
  sorties: any[];
  total_entrees: number;
  total_sorties: number;
  net_change: number;
  // Champs de compatibilité
  initialStock?: number;
  entry?: number;
  exit?: number;
  finalStock?: number;
  netChange?: number;
  stock_initial?: number;
  stock_final?: number;
}

interface MonthlyStockReport {
  produit_id: number;
  produit_nom: string;
  magasin_nom: string;
  stock_initial: number;
  entrees: number;
  sorties: number;
  stock_final: number;
  variation: number;
  // Champs de compatibilité
  month?: string;
  stockAtBeginning?: number;
  entry?: number;
  exit?: number;
  finalStock?: number;
  costPerKg?: number;
  amount?: number;
}

interface DetailedStockRecord {
  id: number;
  reference: string;
  date_mouvement: string;
  produit: {
    id: number;
    nom: string;
    code: string;
    type: string;
  };
  sens: string;
  sens_display: string;
  quantite: number;
  magasin_source?: {
    id: number;
    nom: string;
    code: string;
  };
  magasin_destination?: {
    id: number;
    nom: string;
    code: string;
  };
  pesee?: {
    id: number;
    reference: string;
    date_heure: string;
    poids_net: number;
  };
  operateur?: {
    id: number;
    nom: string;
    username: string;
  };
  type_sortie?: string;
  type_sortie_display?: string;
  observations: string;
  numero_lot: string;
  numero_transfert: string;
  created_at: string;
  // Champs de compatibilité
  date?: string;
  truck?: string;
  productOrigin?: string;
  clientName?: string; // Renommé pour éviter le conflit
  supplier?: string;
  weightIn?: number;
  weightOut?: number;
  bagCount?: number;
  netWeight?: number;
  cumulativeWeight?: number;
  cumulativeBags?: number;
  clients?: string;
  trPercentage?: number;
  travee?: string;
  // Nouvelles données structurées
  camion?: {
    immatriculation: string;
    marque: string;
    modele: string;
  };
  chauffeur?: {
    nom: string;
    prenom: string;
    telephone: string;
  };
  fournisseur?: {
    nom: string;
    adresse: string;
    telephone: string;
  };
  client?: {
    nom: string;
    adresse: string;
    telephone: string;
  };
  exportateur?: {
    nom: string;
    adresse: string;
    telephone: string;
  };
}

const StockManagementPage: React.FC = () => {
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [warehouseStats, setWarehouseStats] = useState<WarehouseStats[]>([]);
  const [dailyMovements, setDailyMovements] = useState<DailyStockMovement[]>([]);
  const [monthlyReports, setMonthlyReports] = useState<MonthlyStockReport[]>([]);
  const [detailedRecords, setDetailedRecords] = useState<DetailedStockRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedView, setSelectedView] = useState<'overview' | 'products' | 'warehouses' | 'movements' | 'reports'>('overview');
  const [selectedReportType, setSelectedReportType] = useState<'daily' | 'monthly' | 'detailed'>('daily');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // États pour le modal de modification des seuils
  const [showEditThresholdsModal, setShowEditThresholdsModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState<StockItem | null>(null);

  // Fonction utilitaire pour formater les dates de manière sécurisée
  const formatDateSafely = (dateString: string, formatString: string): string => {
    try {
      if (!dateString) {
        return 'Date non disponible';
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      return format(date, formatString);
    } catch (error) {
      return 'Date invalide';
    }
  };

  // Charger les données depuis l'API
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
 
    setIsLoading(true);
    setError('');
    
    try {
      
      const [stocksData, movementsData, magasinsData] = await Promise.all([
        stockService.getStocks(),
        stockService.getMouvementsStock(),
        stockService.getMagasins()
      ]);

  
      
      if (!Array.isArray(stocksData)) {
        console.error('stocksData n\'est pas un tableau:', stocksData);
        throw new Error('Format de données invalide pour les stocks');
      }
      
      if (!Array.isArray(movementsData)) {
        console.error('movementsData n\'est pas un tableau:', movementsData);
        throw new Error('Format de données invalide pour les mouvements');
      }
      
    
      if (!Array.isArray(magasinsData)) {
        console.error('magasinsData n\'est pas un tableau:', magasinsData);
        throw new Error('Format de données invalide pour les magasins');
      }

      // Transformer les données de stock
      const transformedStocks: StockItem[] = stocksData.map(stock => {
        const stockAny = stock as any; // Assertion de type pour les nouveaux champs
        return {
          ...stock,
          // Utiliser les champs du backend en priorité
          produit_nom: stockAny.produit_nom || stock.produit_details?.nom,
          produit_type_display: stockAny.produit_type || stock.produit_details?.type,
          magasin_nom: stockAny.magasin_nom || stock.magasin_details?.nom,
          status: calculateStockStatus(stock.stock_actuel, stock.stock_minimum, stock.stock_maximum),
          // Utiliser les vraies valeurs du backend si disponibles, sinon calculer des valeurs par défaut
          minThreshold: stock.stock_minimum ?? stockAny.stock_minimum ?? calculateMinThreshold(stock.stock_actuel),
          maxCapacity: stock.stock_maximum ?? stockAny.stock_maximum ?? (stock.magasin_details?.capacite_maximale ? stock.magasin_details.capacite_maximale * 1000 : null),
          lastMovement: getLastMovementForStock(stock.id, movementsData)
        };
      });

      // Transformer les mouvements de stock avec informations complètes
      const transformedMovements: StockMovement[] = movementsData.map((movement) => {
    
        const movementAny = movement as any; // Assertion de type pour les nouveaux champs
        
        // Déterminer le sens en vérifiant toutes les sources possibles
        let sens: string = movement.sens || movementAny.sens || '';
        
        // Si sens n'est pas trouvé, essayer sens_display
        if (!sens && movementAny.sens_display) {
          sens = movementAny.sens_display === 'Entrée' ? 'ENTREE' : 'SORTIE';
        }
        
        // Normaliser le sens (gérer toutes les variantes possibles)
        const sensLower = typeof sens === 'string' ? sens.toLowerCase() : '';
        const normalizedSens = (sens === 'ENTREE' || sens === 'Entrée' || sensLower === 'entree') ? 'ENTREE' : 'SORTIE';
        const isEntree = normalizedSens === 'ENTREE';
        const type = isEntree ? 'in' : 'out';
        
       
        
        return {
          ...movement,
          // Normaliser le champ sens pour garantir la cohérence (après le spread pour écraser)
          sens: normalizedSens,
          productName: movementAny.produit_nom || movement.produit_details?.nom || 'Produit inconnu',
          productType: movementAny.produit_type_display || (movement.produit_details as any)?.get_type_display?.() || '',
          productCategory: movementAny.produit_categorie || (movement.produit_details as any)?.categorie || '',
          productDescription: movementAny.produit_description || (movement.produit_details as any)?.description || '',
          peseeWeight: movementAny.pesee_poids_net || (movement.pesee_details as any)?.poids_net || 0,
          type: type,
          direction: movementAny.sens_display || (isEntree ? 'Entrée' : 'Sortie'),
          user: movementAny.operateur_nom || movement.utilisateur_details?.username || 'Utilisateur inconnu',
          userEmail: movementAny.operateur_email || movement.utilisateur_details?.email || '',
          location: movementAny.magasin_destination_nom || movementAny.magasin_source_nom || movement.magasin_destination_details?.nom || movement.magasin_source_details?.nom || 'Magasin inconnu',
          locationType: movementAny.magasin_destination_type || movementAny.magasin_source_type || '',
          date: movementAny.date_mouvement_display || formatDateSafely(movement.date_mouvement, 'dd/MM/yyyy HH:mm') || 'Date indisponible',
          quantity: parseFloat(movement.quantite.toString()) || 0,
          // Nouvelles données du camion
          camion_immatriculation: movementAny.camion_immatriculation || '',
          camion_marque: movementAny.camion_marque || '',
          camion_modele: movementAny.camion_modele || '',
          // Nouvelles données du chauffeur
          chauffeur_nom: movementAny.chauffeur_nom || '',
          chauffeur_prenom: movementAny.chauffeur_prenom || '',
          chauffeur_telephone: movementAny.chauffeur_telephone || '',
          // Nouvelles données du fournisseur
          fournisseur_nom: movementAny.fournisseur_nom || '',
          fournisseur_adresse: movementAny.fournisseur_adresse || '',
          fournisseur_contact: movementAny.fournisseur_contact || '',
          // Nouvelles données du client
          client_nom: movementAny.client_nom || '',
          client_adresse: movementAny.client_adresse || '',
          client_telephone: movementAny.client_telephone || '',
          // Nouvelles données de l'exportateur
          exportateur_nom: movementAny.exportateur_nom || '',
          exportateur_adresse: movementAny.exportateur_adresse || '',
          exportateur_telephone: movementAny.exportateur_telephone || ''
        };
      });
      
     
      // Transformer les statistiques des magasins
      const transformedWarehouses: WarehouseStats[] = magasinsData.map(magasin => {
        const stocksInMagasin = transformedStocks.filter(stock => {
          // Utiliser le champ magasin (ID) si disponible, sinon utiliser le nom
          if (stock.magasin !== undefined) {
           
            return stock.magasin === magasin.id;
          } else {
            // Fallback : utiliser le nom du magasin
           
            return stock.magasin_nom === magasin.nom;
          }
        });
        
       
       
        const usedCapacity = stocksInMagasin.reduce((sum, stock) => {
          // Convertir stock_actuel en nombre et gérer les valeurs négatives
          const stockActuel = parseFloat(stock.stock_actuel.toString()) || 0;
          // Utiliser la valeur absolue pour éviter les problèmes avec les stocks négatifs
          return sum + Math.abs(stockActuel);
        }, 0);
        const criticalItems = stocksInMagasin.filter(stock => stock.status === 'critical').length;

        const result = {
          ...magasin,
          usedCapacity: usedCapacity,
          productCount: stocksInMagasin.length,
          criticalItems: criticalItems
        };
        
        return result;
      });

      
      
      setStockItems(transformedStocks);
      setStockMovements(transformedMovements);
      setWarehouseStats(transformedWarehouses);
  
      // Charger les rapports avec les données de mouvements
      await loadReports(transformedMovements);

    } catch (err: any) {
      console.error('❌ Erreur dans loadData:', err);
      if (err.message.includes('Authentification requise')) {
        setError('Vous devez être connecté pour accéder à cette page. Veuillez vous connecter.');
      } else {
        setError('Erreur lors du chargement des données: ' + err.message);
      }
      console.error('Erreur lors du chargement des données:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonctions de génération de données basées sur les vraies données
  const generateRealDailyMovements = (startDate: Date, endDate: Date, movementsData?: StockMovement[]): DailyStockMovement[] => {
   
    const movements: DailyStockMovement[] = [];
    const currentDate = new Date(startDate);
    
    // Filtrer les mouvements pour cette date
    const movementsToUse = movementsData || stockMovements;
   
    
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      
      const dayMovements = movementsToUse.filter(movement => {
        // Exclure les mouvements avec quantité = 0
        if (movement.quantity === 0) return false;
        
        // Utiliser date_mouvement_display ou date en priorité
        const dateToUse = movement.date_mouvement_display || movement.date || movement.date_mouvement;
        if (!dateToUse) return false;
        
        // Parser la date française (dd/MM/yyyy HH:mm) ou ISO
        let movementDate;
        if (dateToUse.includes('/')) {
          // Format français: "11/10/2025 13:13"
          const [datePart] = dateToUse.split(' ');
          const [day, month, year] = datePart.split('/');
          movementDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        } else {
          // Format ISO
          movementDate = new Date(dateToUse);
        }
        
        if (isNaN(movementDate.getTime())) return false;
        return format(movementDate, 'yyyy-MM-dd') === dateStr;
        });
      
      
      // Calculer les totaux pour cette journée
      const entrees = dayMovements.filter(m => m.type === 'in');
      const sorties = dayMovements.filter(m => m.type === 'out');
      
      const totalEntrees = entrees.reduce((sum, m) => sum + Math.abs(m.quantity || 0), 0);
      const totalSorties = sorties.reduce((sum, m) => sum + Math.abs(m.quantity || 0), 0);
      
      
      // Calculer le stock initial (mouvements avant cette date)
      const stockInitial = movementsToUse
        .filter(m => {
          const dateToUse = m.date_mouvement_display || m.date || m.date_mouvement;
          if (!dateToUse) return false;
          
          let movementDate;
          if (dateToUse.includes('/')) {
            const [datePart] = dateToUse.split(' ');
            const [day, month, year] = datePart.split('/');
            movementDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
          } else {
            movementDate = new Date(dateToUse);
          }
          
          if (isNaN(movementDate.getTime())) return false;
          return movementDate < new Date(dateStr);
        })
        .reduce((sum, m) => {
          if (m.type === 'in') return sum + Math.abs(m.quantity || 0);
          if (m.type === 'out') return sum - Math.abs(m.quantity || 0);
          return sum;
        }, 0);
      
      // Calculer le stock final : Stock Initial + Entrées - Sorties
      const stockFinal = stockInitial + totalEntrees - totalSorties;
      
      movements.push({
        date: dateStr,
        entrees: entrees.map(e => ({
          produit: e.productName || 'Produit inconnu',
          quantite: e.quantity || 0,
          reference: e.reference || ''
        })),
        sorties: sorties.map(s => ({
          produit: s.productName || 'Produit inconnu',
          quantite: s.quantity || 0,
          reference: s.reference || ''
        })),
        total_entrees: totalEntrees,
        total_sorties: totalSorties,
        net_change: totalEntrees - totalSorties,
        // Champs de compatibilité
        initialStock: stockInitial,
        entry: totalEntrees,
        exit: totalSorties,
        finalStock: stockFinal,
        netChange: totalEntrees - totalSorties,
        stock_initial: stockInitial,
        stock_final: stockFinal
      });
      
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
   
    // Filtrer les dates qui ont des mouvements avec des quantités ≠ 0
    const filteredMovements = movements.filter(movement => 
      movement.total_entrees > 0 || movement.total_sorties > 0
    );
  
    return filteredMovements;
  };

  const generateRealMonthlyReports = (_year: number, movementsData?: StockMovement[]): MonthlyStockReport[] => {
    // Grouper les mouvements par produit
    const movementsToUse = movementsData || stockMovements;
    const productMovements = movementsToUse
      .filter(movement => movement.quantity !== 0) // Exclure les mouvements avec quantité = 0
      .reduce((acc, movement) => {
        const productName = movement.productName || 'Produit inconnu';
        if (!acc[productName]) {
          acc[productName] = {
            produit_id: movement.produit || 0,
            produit_nom: productName,
            magasin_nom: movement.location || 'Magasin inconnu',
            entrees: 0,
            sorties: 0,
            stock_initial: 0,
            stock_final: 0
          };
        }
        
        if (movement.type === 'in') {
          acc[productName].entrees += Math.abs(movement.quantity || 0);
        } else {
          acc[productName].sorties += Math.abs(movement.quantity || 0);
        }
        
        return acc;
      }, {} as Record<string, any>);
    
    // Convertir en tableau et calculer les variations
    return Object.values(productMovements).map((product: any) => ({
      ...product,
      variation: product.entrees - product.sorties,
      // Champs de compatibilité
      month: product.produit_nom,
      stockAtBeginning: product.stock_initial,
      entry: product.entrees,
      exit: product.sorties,
      finalStock: product.stock_final,
      costPerKg: 0, // À calculer si nécessaire
      amount: 0 // À calculer si nécessaire
    }));
  };

  const generateRealDetailedRecords = (startDate: Date, endDate: Date, movementsData?: StockMovement[]): DetailedStockRecord[] => {
    // Filtrer les mouvements dans la période
    const movementsToUse = movementsData || stockMovements;
    const periodMovements = movementsToUse.filter(movement => {
      // Exclure les mouvements avec quantité = 0
      if (movement.quantity === 0) return false;
      
      // Utiliser date_mouvement_display ou date en priorité
      const dateToUse = movement.date_mouvement_display || movement.date || movement.date_mouvement;
      if (!dateToUse) return false;
      
      // Parser la date française (dd/MM/yyyy HH:mm) ou ISO
      let movementDate;
      if (dateToUse.includes('/')) {
        // Format français: "11/10/2025 13:13"
        const [datePart] = dateToUse.split(' ');
        const [day, month, year] = datePart.split('/');
        movementDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      } else {
        // Format ISO
        movementDate = new Date(dateToUse);
      }
      
      if (isNaN(movementDate.getTime())) return false;
      return movementDate >= startDate && movementDate <= endDate;
    });
    
    // Trier les mouvements par date croissante pour calculer le cumul correctement
    const sortedMovements = [...periodMovements].sort((a, b) => {
      const dateA = a.date_mouvement_display || a.date || a.date_mouvement || '';
      const dateB = b.date_mouvement_display || b.date || b.date_mouvement || '';
      
      // Parser les dates françaises ou ISO
      let parsedDateA, parsedDateB;
      if (dateA.includes('/')) {
        const [datePartA] = dateA.split(' ');
        const [dayA, monthA, yearA] = datePartA.split('/');
        parsedDateA = new Date(`${yearA}-${monthA.padStart(2, '0')}-${dayA.padStart(2, '0')}`);
      } else {
        parsedDateA = new Date(dateA);
      }
      
      if (dateB.includes('/')) {
        const [datePartB] = dateB.split(' ');
        const [dayB, monthB, yearB] = datePartB.split('/');
        parsedDateB = new Date(`${yearB}-${monthB.padStart(2, '0')}-${dayB.padStart(2, '0')}`);
      } else {
        parsedDateB = new Date(dateB);
      }
      
      if (isNaN(parsedDateA.getTime()) || isNaN(parsedDateB.getTime())) return 0;
      return parsedDateA.getTime() - parsedDateB.getTime(); // Tri croissant pour le calcul du cumul
    });

    // Calculer le cumul des poids et des sacs
    let cumulativeWeight = 0;
    let cumulativeBags = 0;
    
    const recordsWithCumulative = sortedMovements.map((movement) => {
      const poidsNet = movement.peseeWeight || 0;
      const nombreSacs = movement.nombre_sacs || (movement as any).nb_sacs_acceptes || 0;
      
      
      // Ajouter ou soustraire selon le type de mouvement
      if (movement.type === 'in') {
        cumulativeWeight += poidsNet; // Entrée : ajouter le poids
        cumulativeBags += nombreSacs; // Entrée : ajouter les sacs
      } else {
        cumulativeWeight -= poidsNet; // Sortie : soustraire le poids
        cumulativeBags -= nombreSacs; // Sortie : soustraire les sacs
      }
      
     
      return {
        ...movement,
        cumulativeWeight: cumulativeWeight,
        cumulativeBags: cumulativeBags
      };
    });
    
   
    // Inverser l'ordre pour l'affichage (plus récent en premier)
    const finalRecords = recordsWithCumulative.reverse();

    // Convertir en DetailedStockRecord
    return finalRecords.map(movement => ({
      id: movement.id,
      reference: movement.reference || '',
      date_mouvement: movement.date || formatDateSafely(movement.date_mouvement, 'yyyy-MM-dd HH:mm'),
      produit: {
        id: movement.produit || 0,
        nom: movement.productName || 'Produit inconnu',
        code: '',
        type: movement.productType || ''
      },
      sens: movement.type === 'in' ? 'ENTREE' : 'SORTIE',
      sens_display: movement.direction || (movement.type === 'in' ? 'Entrée' : 'Sortie'),
      quantite: movement.quantity || 0,
      magasin_source: undefined,
      magasin_destination: {
        id: 0,
        nom: movement.location || 'Magasin inconnu',
        code: ''
      },
      pesee: {
        id: movement.pesee || 0,
        reference: movement.pesee_reference || '',
        date_heure: movement.date || '',
        poids_net: movement.peseeWeight || 0
      },
      operateur: {
        id: movement.operateur || 0,
        nom: movement.user || 'Utilisateur inconnu',
        username: movement.user || 'Utilisateur inconnu'
      },
      type_sortie: movement.type_sortie || undefined,
      type_sortie_display: movement.type_sortie_display || undefined,
      observations: movement.observations || '',
      numero_lot: (movement as any).numero_lot || '',
      numero_transfert: (movement as any).numero_transfert || '',
      created_at: movement.created_at || '',
      // Champs de compatibilité
      truck: movement.camion_immatriculation || '', // Utiliser l'immatriculation du camion
      poids_net: movement.peseeWeight || 0,
      prix_unitaire: 0, // Pas disponible
      montant_total: 0, // Pas disponible
      travee: '', // Pas disponible
      // Nouvelles données du camion
      camion: {
        immatriculation: movement.camion_immatriculation || '',
        marque: movement.camion_marque || '',
        modele: movement.camion_modele || ''
      },
      // Nouvelles données du chauffeur
      chauffeur: {
        nom: movement.chauffeur_nom || '',
        prenom: movement.chauffeur_prenom || '',
        telephone: movement.chauffeur_telephone || ''
      },
      // Nouvelles données du fournisseur
      fournisseur: {
        nom: movement.fournisseur_nom || '',
        adresse: movement.fournisseur_adresse || '',
        telephone: movement.fournisseur_contact || ''
      },
      // Nouvelles données du client
      client: {
        nom: movement.client_nom || '',
        adresse: movement.client_adresse || '',
        telephone: movement.client_telephone || ''
      },
      // Nouvelles données de l'exportateur
      exportateur: {
        nom: movement.exportateur_nom || '',
        adresse: movement.exportateur_adresse || '',
        telephone: movement.exportateur_telephone || ''
      }
    })) as DetailedStockRecord[];
  };

  const loadReports = async (movementsData?: StockMovement[]) => {
   
    try {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);

     
      // Charger les rapports avec gestion des erreurs 404
      const [dailyData, monthlyData, detailedData] = await Promise.allSettled([
        stockService.getMouvementsQuotidiens(
          format(thirtyDaysAgo, 'yyyy-MM-dd'),
          format(today, 'yyyy-MM-dd')
        ).catch((err) => {
          console.log('❌ API mouvements quotidiens échouée, utilisation du fallback:', err);
          return generateRealDailyMovements(thirtyDaysAgo, today, movementsData);
        }),
        stockService.getRapportMensuel(today.getFullYear()).catch(() => {
          return generateRealMonthlyReports(today.getFullYear(), movementsData);
        }),
        stockService.getEnregistrementsDetailles({
          date_debut: format(thirtyDaysAgo, 'yyyy-MM-dd'),
          date_fin: format(today, 'yyyy-MM-dd')
        }).catch(() => {
          return generateRealDetailedRecords(thirtyDaysAgo, today, movementsData);
        })
      ]);

      // Extraire les données des promesses résolues
      const dailyResult = dailyData.status === 'fulfilled' ? dailyData.value as any : null;
      const monthlyResult = monthlyData.status === 'fulfilled' ? monthlyData.value as any : null;
      const detailedResult = detailedData.status === 'fulfilled' ? detailedData.value as any : null;
      

    
      const dailyMovementsData = dailyResult?.data?.mouvements_quotidiens || 
        dailyResult?.mouvements_quotidiens || 
        (Array.isArray(dailyResult) ? dailyResult : []);
      
      const monthlyReportsData = monthlyResult?.data?.stocks_par_produit || 
        monthlyResult?.stocks_par_produit || 
        (Array.isArray(monthlyResult) ? monthlyResult : []);
      
      const detailedRecordsData = detailedResult?.data?.enregistrements || 
        detailedResult?.enregistrements || 
        (Array.isArray(detailedResult) ? detailedResult : []);
      

      setDailyMovements(dailyMovementsData);
      setMonthlyReports(monthlyReportsData);
      setDetailedRecords(detailedRecordsData);

    } catch (err: any) {
      console.error('❌ Erreur lors du chargement des rapports:', err);
      // En cas d'erreur générale, utiliser des données de démonstration
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      const mockDaily = generateRealDailyMovements(thirtyDaysAgo, today, movementsData);
      const mockMonthly = generateRealMonthlyReports(today.getFullYear(), movementsData);
      const mockDetailed = generateRealDetailedRecords(thirtyDaysAgo, today, movementsData);
      
     
      setDailyMovements(mockDaily);
      setMonthlyReports(mockMonthly);
      setDetailedRecords(mockDetailed);
    }
  };

  // Fonctions utilitaires pour les calculs
  const calculateStockStatus = (
    currentStock: number, 
    stockMinimum?: number | null, 
    stockMaximum?: number | null
  ): 'normal' | 'low' | 'critical' | 'full' => {
    // Si les seuils sont définis, les utiliser
    if (stockMinimum !== null && stockMinimum !== undefined) {
      if (currentStock <= stockMinimum) return 'critical';
      if (currentStock <= stockMinimum * 1.2) return 'low';
    }
    if (stockMaximum !== null && stockMaximum !== undefined) {
      if (currentStock >= stockMaximum) return 'full';
    }
    
    // Sinon, utiliser les valeurs par défaut
    if (currentStock === 0) return 'critical';
    if (currentStock < 100) return 'critical';
    if (currentStock < 500) return 'low';
    if (currentStock > 4000) return 'full';
    return 'normal';
  };

  const calculateMinThreshold = (currentStock: number): number => {
    // Valeur par défaut calculée si stock_minimum n'est pas défini
    return Math.max(100, Math.floor(currentStock * 0.1));
  };

  const getLastMovementForStock = (stockId: number, movements: MouvementStock[]): any => {
    const stockMovements = movements.filter(m => 
      m.produit === stockId || 
      (m.magasin_source === stockId || m.magasin_destination === stockId)
    );
    
    if (stockMovements.length === 0) return undefined;

    const lastMovement = stockMovements.sort((a, b) => {
      try {
        const dateA = new Date(a.date_mouvement);
        const dateB = new Date(b.date_mouvement);
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          return 0; // Garder l'ordre original si les dates sont invalides
        }
        return dateB.getTime() - dateA.getTime();
      } catch (error) {
        return 0;
      }
    })[0];

    return {
      type: lastMovement.sens === 'ENTREE' ? 'in' : 'out',
      quantity: lastMovement.quantite,
      date: lastMovement.date_mouvement,
      reason: lastMovement.motif
    };
  };

  // Fonctions utilitaires
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal':
        return 'bg-green-100 text-green-800';
      case 'low':
        return 'bg-yellow-100 text-yellow-800';
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'full':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'normal':
        return 'Normal';
      case 'low':
        return 'Stock bas';
      case 'critical':
        return 'Critique';
      case 'full':
        return 'Plein';
      default:
        return 'Inconnu';
    }
  };

  // Calculs des statistiques
  const totalStockValue = stockItems.reduce((sum, item) => 
    sum + (item.stock_actuel * (item.produit_details?.prix_unitaire || 0)), 0
  );

  const criticalItems = stockItems.filter(item => item.status === 'critical').length;
  const lowStockItems = stockItems.filter(item => item.status === 'low').length;
  const fullStockItems = stockItems.filter(item => item.status === 'full').length;

  // Données pour les graphiques
  const stockByProductData = {
    labels: stockItems.map(item => {
      // Utiliser les nouveaux champs du backend en priorité
      const itemAny = item as any;
      return itemAny.produit_nom || item.produit_details?.nom || 'Produit inconnu';
    }),
    datasets: [{
      label: 'Stock actuel (kg)',
      data: stockItems.map(item => item.stock_actuel),
      backgroundColor: [
        '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'
      ],
      borderWidth: 0
    }]
  };

  const stockByWarehouseData = {
    labels: warehouseStats.map(warehouse => warehouse.nom),
    datasets: [{
      label: 'Capacité utilisée (%)',
      data: warehouseStats.map(warehouse => {
        
        if (!warehouse.capacite_maximale || warehouse.capacite_maximale <= 0) {
          return 0;
        }
        
        const capaciteMax = parseFloat(warehouse.capacite_maximale.toString());
        const capaciteUtilisee = warehouse.usedCapacity || 0;
        const pourcentage = Math.round((capaciteUtilisee / capaciteMax) * 100);
      
        return pourcentage;
      }),
      backgroundColor: [
        '#10B981', '#3B82F6', '#F59E0B'
      ],
      borderWidth: 0
    }]
  };

 
  const movementsData = {
    labels: stockMovements.map(movement => 
      movement.date || formatDateSafely(movement.date_mouvement, 'dd/MM')
    ),
    datasets: [
      {
        label: 'Entrées',
        data: stockMovements.map(movement => 
          movement.type === 'in' ? Math.abs(movement.quantity || 0) : 0
        ),
        backgroundColor: '#10B981',
        borderColor: '#10B981',
        tension: 0.4
      },
      {
        label: 'Sorties',
        data: stockMovements.map(movement => 
          movement.type === 'out' ? Math.abs(movement.quantity || 0) : 0
        ),
        backgroundColor: '#EF4444',
        borderColor: '#EF4444',
        tension: 0.4
      }
    ]
  };
  

  const statusDistributionData = {
    labels: ['Normal', 'Stock bas', 'Critique', 'Plein'],
    datasets: [{
      data: [
        stockItems.filter(item => item.status === 'normal').length,
        lowStockItems,
        criticalItems,
        fullStockItems
      ],
      backgroundColor: ['#10B981', '#F59E0B', '#EF4444', '#3B82F6'],
      borderWidth: 0
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 20
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 20
        }
      }
    }
  };

  // Fonctions d'export
  const exportToExcel = (data: any[], filename: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rapport');
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const exportDailyMovements = async () => {
    try {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);

      const blob = await stockService.exportMouvementsQuotidiens(
        format(thirtyDaysAgo, 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
      );
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mouvements_quotidiens.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erreur lors de l\'export des mouvements quotidiens:', error);
      // Fallback vers l'export local
      const exportData = dailyMovements.map(item => ({
        'Date': item.date,
        'Stock Initial (KG)': item.initialStock,
        'Entrée (KG)': item.entry,
        'Sortie (KG)': item.exit,
        'Stock Final (KG)': item.finalStock
      }));
      exportToExcel(exportData, 'mouvements_quotidiens');
    }
  };

  const exportMonthlyReport = async () => {
    try {
      const today = new Date();
      const blob = await stockService.exportRapportMensuel(today.getFullYear());
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rapport_mensuel.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erreur lors de l\'export du rapport mensuel:', error);
      // Fallback vers l'export local
      const exportData = monthlyReports.map(item => ({
        'Mois': item.month,
        'Stock au 1er du mois': item.stockAtBeginning,
        'Entrée': item.entry,
        'Sortie': item.exit,
        'Stock final au 31': item.finalStock,
        'Coût du KG': item.costPerKg,
        'Montant (FCFA)': item.amount
      }));
      exportToExcel(exportData, 'rapport_mensuel');
    }
  };

  const exportDetailedRecords = () => {
    const exportData = detailedRecords.map(item => ({
      'Date': item.date,
      'Camion': item.truck,
      'N° F': item.reference,
      'Origine du produit': item.productOrigin,
      'Client/FR': item.client,
      'Fournisseur': item.supplier,
      'Poids entré (KG)': item.weightIn,
      'Poids sorti (KG)': item.weightOut,
      'Nbre SACS': item.bagCount,
      'Poids net (KG)': item.netWeight,
      'Cumul poids (KG)': item.cumulativeWeight,
      'Cumul sac': item.cumulativeBags,
      'Clients': item.clients,
      'TR (%)': item.trPercentage,
      'Travee': item.travee
    }));
    exportToExcel(exportData, 'enregistrements_detaille');
  };

  const filteredItems = stockItems.filter(item => {
    const itemAny = item as any;
    const productName = itemAny.produit_nom || item.produit_details?.nom || '';
    const productType = itemAny.produit_type_display || item.produit_details?.type || '';
    const productCategory = itemAny.produit_categorie || item.produit_details?.categorie || '';
    const location = itemAny.magasin_nom || item.magasin_details?.nom || '';
    
    const matchesSearch = productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         productType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         productCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Composant pour la vue d'ensemble
  const OverviewView = () => (
    <div className="space-y-6">
      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <CubeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Produits</p>
                <p className="text-2xl font-bold text-gray-900">{stockItems.length}</p>
              </div>
            </div>
          </div>
          
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valeur Totale</p>
                <p className="text-2xl font-bold text-gray-900">
                {totalStockValue.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                </p>
              </div>
            </div>
          </div>
          
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Stock Critique</p>
              <p className="text-2xl font-bold text-gray-900">{criticalItems}</p>
            </div>
            </div>
          </div>
          
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Entrepôts</p>
              <p className="text-2xl font-bold text-gray-900">{warehouseStats.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par statut */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Statut</h3>
          <div className="h-64">
            <Doughnut data={statusDistributionData} options={pieOptions} />
          </div>
        </div>

        {/* Stock par produit */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Stock par Produit</h3>
          <div className="h-64">
            <Bar data={stockByProductData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Mouvements récents */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Mouvements Récents</h3>
          <button 
            onClick={() => setSelectedView('movements')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Voir tout
          </button>
        </div>
        <div className="space-y-3">
          {stockMovements.slice(0, 5).map((movement) => (
            <div key={movement.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className={`p-2 rounded-full mr-3 ${
                  movement.type === 'in' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {movement.type === 'in' ? (
                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{movement.productName || 'Produit inconnu'}</p>
                  <p className="text-sm text-gray-600">{movement.motif}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-semibold ${
                  movement.type === 'in' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {movement.type === 'in' ? '+' : '-'}{movement.quantite} kg
                </p>
                <p className="text-sm text-gray-500">
                  {movement.date || formatDateSafely(movement.date_mouvement, 'dd/MM/yyyy')}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Composant pour la vue par produits
  const ProductsView = () => (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par produit, type ou emplacement..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="normal">Normal</option>
                  <option value="low">Stock bas</option>
                  <option value="critical">Critique</option>
                  <option value="full">Plein</option>
                </select>
              </div>
            </div>
          </div>
        </div>

      {/* Tableau des produits */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Actuel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seuils
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Emplacement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernière MAJ
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {(() => {
                          const itemAny = item as any;
                          return itemAny.produit_nom || item.produit_details?.nom || 'Produit inconnu';
                        })()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {(() => {
                          const itemAny = item as any;
                          return itemAny.produit_type_display || item.produit_details?.type || 'Type inconnu';
                        })()}
                      </div>
                      {(() => {
                        const itemAny = item as any;
                        const categorie = itemAny.produit_categorie || item.produit_details?.categorie;
                        return categorie && (
                          <div className="text-xs text-gray-400">Catégorie: {categorie}</div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {item.stock_actuel.toLocaleString()} {(() => {
                        const itemAny = item as any;
                        return itemAny.produit_unite || item.produit_details?.unite || 'kg';
                      })()}
                    </div>
                    {(() => {
                      const itemAny = item as any;
                      const prixUnitaire = itemAny.produit_prix_unitaire || item.produit_details?.prix_unitaire;
                      return prixUnitaire && (
                        <div className="text-xs text-gray-500">
                          {prixUnitaire.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}/kg
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div>
                        Min: {(() => {
                          const itemAny = item as any;
                          // Utiliser stock_minimum du backend si disponible, sinon la valeur calculée
                          const stockMin = itemAny.stock_minimum ?? item.minThreshold;
                          return stockMin ? stockMin.toLocaleString() : 'N/A';
                        })()} {(() => {
                          const itemAny = item as any;
                          return itemAny.produit_unite || item.produit_details?.unite || 'kg';
                        })()}
                      </div>
                      <div>
                        Max: {(() => {
                          const itemAny = item as any;
                          // Utiliser stock_maximum du backend si disponible, sinon la valeur calculée
                          const stockMax = itemAny.stock_maximum ?? item.maxCapacity;
                          return stockMax ? stockMax.toLocaleString() : 'N/A';
                        })()} {(() => {
                          const itemAny = item as any;
                          return itemAny.produit_unite || item.produit_details?.unite || 'kg';
                        })()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(() => {
                      const itemAny = item as any;
                      return itemAny.magasin_nom || item.magasin_details?.nom || 'Magasin inconnu';
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status || 'normal')}`}>
                      {getStatusLabel(item.status || 'normal')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(item.date_mise_a_jour).toLocaleDateString('fr-FR')}
                  </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          onClick={() => {
                            setSelectedStock(item);
                            setShowEditThresholdsModal(true);
                          }}
                          className="text-orange-600 hover:text-orange-900 p-1"
                          title="Modifier les seuils"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun produit trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? 'Essayez de modifier vos critères de recherche.' 
                : 'Commencez par ajouter un nouveau produit au stock.'}
            </p>
          </div>
        )}
    </div>
  );

  // Composant pour la vue par entrepôts
  const WarehousesView = () => {
   
    return (
    <div className="space-y-6">
      {/* Graphique de capacité des entrepôts */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Capacité des Entrepôts</h3>
        <div className="h-64">
          <Bar data={stockByWarehouseData} options={chartOptions} />
        </div>
      </div>

      {/* Cartes des entrepôts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {warehouseStats.map((warehouse) => (
          <div key={warehouse.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{warehouse.nom}</h3>
              <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Capacité utilisée</span>
                  <span>
                    {(() => {
                      if (!warehouse.capacite_maximale || warehouse.capacite_maximale <= 0) {
                        return '0%';
                      }
                      const capaciteMax = parseFloat(warehouse.capacite_maximale.toString());
                      const capaciteUtilisee = warehouse.usedCapacity || 0;
                      const pourcentage = Math.round((capaciteUtilisee / capaciteMax) * 100);
                      return `${pourcentage}%`;
                    })()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ 
                      width: `${(() => {
                        if (!warehouse.capacite_maximale || warehouse.capacite_maximale <= 0) {
                          return 0;
                        }
                        const capaciteMax = parseFloat(warehouse.capacite_maximale.toString());
                        const capaciteUtilisee = warehouse.usedCapacity || 0;
                        return Math.round((capaciteUtilisee / capaciteMax) * 100);
                      })()}%` 
                    }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {warehouse.usedCapacity?.toLocaleString() || 0} / {warehouse.capacite_maximale ? parseFloat(warehouse.capacite_maximale.toString()).toLocaleString() : 'N/A'} kg
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{warehouse.productCount || 0}</p>
                  <p className="text-sm text-gray-600">Produits</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{warehouse.criticalItems || 0}</p>
                  <p className="text-sm text-gray-600">Critiques</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  // Composant pour la vue des rapports
  const ReportsView = () => (
    <div className="space-y-6">
      {/* Sélection du type de rapport */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Types de Rapports</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setSelectedReportType('daily')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              selectedReportType === 'daily'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-center">
              <ClockIcon className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <h4 className="font-medium text-gray-900">Mouvements Quotidiens</h4>
              <p className="text-sm text-gray-600">Suivi journalier du stock</p>
            </div>
          </button>
          
          <button
            onClick={() => setSelectedReportType('monthly')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              selectedReportType === 'monthly'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-center">
              <ChartBarIcon className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <h4 className="font-medium text-gray-900">Rapport Mensuel</h4>
              <p className="text-sm text-gray-600">État annuel du magasin</p>
            </div>
          </button>
          
          <button
            onClick={() => setSelectedReportType('detailed')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              selectedReportType === 'detailed'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-center">
              <CubeIcon className="h-8 w-8 mx-auto mb-2 text-orange-600" />
              <h4 className="font-medium text-gray-900">Enregistrements Détaillés</h4>
              <p className="text-sm text-gray-600">Suivi détaillé par camion</p>
            </div>
          </button>
        </div>
      </div>

      {/* Rapport des mouvements quotidiens */}
      {selectedReportType === 'daily' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">MOUVEMENTS DU STOCK</h3>
              <button
                onClick={exportDailyMovements}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Exporter Excel
              </button>
            </div>
            
            {/* Résumé */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-yellow-100 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Stock Initial</p>
                <p className="text-xl font-bold text-gray-900">
                  {dailyMovements[0]?.stock_initial?.toLocaleString() || '0'} KG
                </p>
              </div>
              <div className="bg-green-100 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Quantité Entrée</p>
                <p className="text-xl font-bold text-gray-900">
                  {Array.isArray(dailyMovements) ? dailyMovements.reduce((sum, item) => sum + (item.total_entrees || 0), 0).toLocaleString() : '0'} KG
                </p>
              </div>
              <div className="bg-red-100 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Quantité Sortie</p>
                <p className="text-xl font-bold text-gray-900">
                  {Array.isArray(dailyMovements) ? dailyMovements.reduce((sum, item) => sum + (item.total_sorties || 0), 0).toLocaleString() : '0'} KG
                </p>
              </div>
              <div className="bg-blue-100 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Stock Final</p>
                <p className="text-xl font-bold text-gray-900">
                  {Array.isArray(dailyMovements) && dailyMovements.length > 0 ? dailyMovements[dailyMovements.length - 1]?.stock_final?.toLocaleString() || '0' : '0'} KG
                </p>
              </div>
            </div>

            {/* Tableau des mouvements */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-orange-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Initial (KG)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entrée (KG)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sortie (KG)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Final (KG)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(dailyMovements) ? dailyMovements
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Trier par date décroissante
                    .map((movement, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{movement.date}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{(movement.stock_initial || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 font-medium">{(movement.total_entrees || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600 font-medium">{(movement.total_sorties || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">{(movement.stock_final || 0).toLocaleString()}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-3 text-center text-sm text-gray-500">
                        Aucune donnée disponible
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Rapport mensuel */}
      {selectedReportType === 'monthly' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">ETAT ANNUEL DU MAGASIN CITA - ANNEE 2025</h3>
              <button
                onClick={exportMonthlyReport}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Exporter Excel
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période 2025</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock au 1er du mois</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entrée</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sortie</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock final au 31</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-red-100">Coût du KG</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-red-100">Montant (FCFA)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(monthlyReports) ? monthlyReports.map((report, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{report.produit_nom || `Produit ${index + 1}`}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{(report.stock_initial || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 font-medium">{(report.entrees || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600 font-medium">{(report.sorties || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">{(report.stock_final || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-red-50">{(report.costPerKg || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium bg-red-50">{(report.amount || 0).toLocaleString()}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-3 text-center text-sm text-gray-500">
                        Aucune donnée disponible
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Signatures */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="border-t-2 border-gray-400 pt-2">
                  <p className="text-sm font-medium text-gray-900">VISA DU GESTIONNAIRE DE STOCK CITA</p>
                </div>
              </div>
              <div className="text-center">
                <div className="border-t-2 border-gray-400 pt-2">
                  <p className="text-sm font-medium text-gray-900">VISA CHEF DE PRODUCTION CITA</p>
                </div>
              </div>
              <div className="text-center">
                <div className="border-t-2 border-gray-400 pt-2">
                  <p className="text-sm font-medium text-gray-900">VISA RAF CITA</p>
                </div>
              </div>
              <div className="text-center">
                <div className="border-t-2 border-gray-400 pt-2">
                  <p className="text-sm font-medium text-gray-900">VISA DIR CITA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enregistrements détaillés */}
      {selectedReportType === 'detailed' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Enregistrements Détaillés</h3>
              <button
                onClick={exportDetailedRecords}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Exporter Excel
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DATES</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CAMION</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° F</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ORIGINE DU PRODUIT</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLIENT/FR</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FOURNISSEUR</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">POIDS ENTRE(KG)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">POIDS SORTI(KG)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nbre SACS</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">POIDS NET(KG)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CUMUL POIDS(KG)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CUMUL SAC</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLIENTS</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TR(%)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-red-100">TRAVEE</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(detailedRecords) ? detailedRecords
                    .sort((a, b) => {
                      const dateA = new Date(a.date_mouvement || a.date || '');
                      const dateB = new Date(b.date_mouvement || b.date || '');
                      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
                      return dateB.getTime() - dateA.getTime(); // Trier par date décroissante
                    })
                    .map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{record.date_mouvement || record.date}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const camion = (record as any).camion;
                          
                          if (camion?.immatriculation) {
                            return `${camion.immatriculation}${camion.marque ? ` (${camion.marque})` : ''}`;
                          }
                          return record.truck || record.pesee?.reference || '-';
                        })()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{record.reference}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{record.produit?.nom || record.productOrigin}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{(record as any).client?.nom || (typeof record.client === 'string' ? record.client : '') || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{(record as any).fournisseur?.nom || (typeof record.supplier === 'string' ? record.supplier : '') || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-green-600 font-medium">{(record.sens === 'ENTREE' ? record.quantite : 0).toLocaleString()}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-red-600 font-medium">{(record.sens === 'SORTIE' ? record.quantite : 0).toLocaleString()}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{(record.pesee as any)?.nombre_sacs || record.bagCount || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">{record.pesee?.poids_net?.toLocaleString() || record.netWeight?.toLocaleString() || '0'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">{(record as any).cumulative_weight?.toLocaleString() || record.cumulativeWeight?.toLocaleString() || '0'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">{(record as any).cumulative_bags?.toLocaleString() || record.cumulativeBags?.toLocaleString() || '0'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{record.clients || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{record.trPercentage || '0'}%</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium bg-red-50">{record.travee || '-'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={14} className="px-3 py-2 text-center text-sm text-gray-500">
                        Aucune donnée disponible
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Composant pour la vue des mouvements
  const MovementsView = () => (
    <div className="space-y-6">
      {/* Graphique des mouvements */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mouvements de Stock</h3>
        <div className="h-64">
          <Line data={movementsData} options={chartOptions} />
        </div>
      </div>

      {/* Tableau des mouvements */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Historique des Mouvements</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Raison
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stockMovements.map((movement) => (
                <tr key={movement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{movement.productName || 'Produit inconnu'}</div>
                    <div className="text-sm text-gray-500">{movement.location || 'Magasin inconnu'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      (movement.sens === 'ENTREE' || movement.type === 'in')
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {(movement.sens === 'ENTREE' || movement.type === 'in') ? 'Entrée' : 'Sortie'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-semibold ${
                      (movement.sens === 'ENTREE' || movement.type === 'in') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(movement.sens === 'ENTREE' || movement.type === 'in') ? '+' : '-'}{movement.quantite} kg
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {movement.motif}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {movement.user || 'Utilisateur inconnu'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {movement.date || formatDateSafely(movement.date_mouvement, 'dd/MM/yyyy HH:mm')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Affichage de chargement
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données de stock...</p>
        </div>
      </div>
    );
  }

  // Affichage d'erreur
  if (error) {
    const isAuthError = error.includes('connecté');
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Erreur de chargement</h3>
            <p className="mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={loadData}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Réessayer
              </button>
              {isAuthError && (
                <button
                  onClick={() => window.location.href = '/login'}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Se connecter
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <CubeIcon className="h-8 w-8 mr-3 text-blue-600" />
                Gestion de Stock
              </h1>
              <p className="text-gray-600 mt-2">
                Gérez l'inventaire et le stockage de vos produits avec des visualisations avancées
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={loadData}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
              >
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Actualiser
              </button>
              <Link 
                to="/stocks"
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center font-medium"
              >
                <ArrowRightIcon className="h-5 w-5 mr-2" />
                Nouveau mouvement
              </Link>
              <Link 
                to="/nouvelle-pesee"
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center font-medium"
              >
                <ScaleIcon className="h-5 w-5 mr-2" />
                Nouvelle pesée
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation des vues */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: ChartBarIcon },
                { id: 'products', label: 'Par Produit', icon: CubeIcon },
                { id: 'warehouses', label: 'Par Entrepôt', icon: BuildingOfficeIcon },
                { id: 'movements', label: 'Mouvements', icon: ArrowPathIcon },
                { id: 'reports', label: 'Rapports', icon: DocumentTextIcon }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setSelectedView(id as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                    selectedView === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Contenu des vues */}
        {selectedView === 'overview' && <OverviewView />}
        {selectedView === 'products' && <ProductsView />}
        {selectedView === 'warehouses' && <WarehousesView />}
        {selectedView === 'movements' && <MovementsView />}
        {selectedView === 'reports' && <ReportsView />}
      </div>

      {/* Modal de modification des seuils */}
      {selectedStock && (
        <EditStockThresholdsModal
          isOpen={showEditThresholdsModal}
          onClose={() => {
            setShowEditThresholdsModal(false);
            setSelectedStock(null);
          }}
          stock={selectedStock as any}
          onSuccess={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default StockManagementPage;
